import * as AlertDialog from '@radix-ui/react-alert-dialog';
import { useState } from 'react';
import s from './DeleteClientAlert.module.scss';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  client: { id: string; name: string };
  onDeleted: () => void;
}

export function DeleteClientAlert({ open, onOpenChange, client, onDeleted }: Props) {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleConfirm(): Promise<void> {
    setDeleting(true);
    setError(null);
    try {
      // DELETE doesn't have a response body, so we don't pass a schema.
      // apiMutation will try to parse JSON and fail on 204; use raw fetch.
      const res = await fetch(`/api/clients/${client.id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) {
        throw new Error(`${res.status} ${res.statusText}`);
      }
      onDeleted();
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setDeleting(false);
    }
  }

  return (
    <AlertDialog.Root open={open} onOpenChange={onOpenChange}>
      <AlertDialog.Portal>
        <AlertDialog.Overlay className={s.overlay} />
        <AlertDialog.Content className={s.content}>
          <AlertDialog.Title className={s.title}>Delete this client?</AlertDialog.Title>
          <AlertDialog.Description className={s.description}>
            <strong>{client.name}</strong> will be marked inactive. Existing data is preserved for
            audit. You can reactivate it later by editing the client.
          </AlertDialog.Description>

          {error && (
            <div role="alert" style={{ color: 'var(--danger-500)', marginBottom: 16 }}>
              Could not delete: {error}
            </div>
          )}

          <div className={s.actions}>
            <AlertDialog.Cancel asChild>
              <button type="button" className={s.cancel} disabled={deleting}>
                Cancel
              </button>
            </AlertDialog.Cancel>
            <button
              type="button"
              className={s.confirm}
              onClick={() => {
                void handleConfirm();
              }}
              disabled={deleting}
            >
              {deleting ? 'Deleting…' : 'Delete'}
            </button>
          </div>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}
